/* layal-hero-couleurs.js : roue de coloris du hero (produit, palette, filigrane, panier).
 *
 * Copie de preview/assets/hero-couleurs.js (maquette validée), branchée sur Shopify :
 * - la section sections/layal-hero.liquid rend les cartes (une par valeur de l'option Couleur) et le JSON
 *   <script type="application/json" data-hc-donnees> : pour chaque coloris id, nom, image, vignette, palette,
 *   variante (id Shopify), prix (texte), disponible. Ce fichier ne contient aucune donnée produit ;
 * - couleur de départ : ?couleur=<id> ou ?variant=<id de variante>, sinon la couleur de la variante choisie ;
 * - un choix de la visiteuse met l'adresse à jour avec ?variant=<id> (et non plus ?couleur=) ;
 * - « Ajouter au panier » : vrai ajout (Layal.ajouterAuPanier, assets/layal-commun.js), tiroir panier du thème ;
 *   le compteur du panier ([data-cart-count]) est tenu à jour par assets/theme.js ;
 * - recherche : vraie recherche de la boutique ; éditeur : un bloc « Couleur » sélectionné affiche sa couleur ;
 * - JSON « lot » (réglage « Le bouton ajoute », ex. voile aimanté ; absent sur le maintien) : le bouton affiche le prix
 *   de la formule et l'ajoute par le bloc d'achat de la page (lpChoisirQuantite puis lpAjouter, assets/layal-produit.js),
 *   dans la couleur affichée, que le bloc d'achat suit déjà. Sans bloc d'achat : la page descend vers les offres.
 */
(function () {
  "use strict";

  // Clés de palette du JSON -> variables CSS posées sur .hc (voir @property dans hero-couleurs.css).
  var VARIABLES = {
    cadre: "--cadre",
    scene: "--scene",
    scene2: "--scene-2",
    carte: "--carte",
    texte: "--texte",
    accent: "--accent",
    verre: "--verre",
    bouton: "--bouton",
    surBouton: "--sur-bouton"
  };
  var CRAN_CACHE = 3; // avec 6 cartes : crans -2..+2 visibles, +3 caché (derrière le produit)

  function normaliser(texte) {
    return String(texte || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .trim();
  }

  function animationsReduites() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function dureeCss(racine, nom, defaut) {
    var v = getComputedStyle(racine).getPropertyValue(nom).trim();
    var n = parseFloat(v);
    if (isNaN(n)) return defaut;
    return /ms$/.test(v) ? n : n * 1000;
  }

  // Préchargement différé des images détourées (une par coloris, ~200 Ko chacune). Seule celle du coloris affiché
  // part avec la page (balise <img fetchpriority="high"> du hero). Les autres sont demandées par lancer(index) :
  // les deux voisines tout de suite (coloris suivant et précédent : glissé, défilement automatique), puis le reste
  // quand le navigateur est inactif. voisines(index) après chaque choix garde la suivante prête. Un coloris choisi
  // avant l'arrivée de son image s'affiche sans trou : l'image en place reste jusqu'au décodage (majProduit).
  // fenetre : window (Image, requestIdleCallback, setTimeout), injecté pour les tests.
  function prechargeurImages(liste, fenetre) {
    var demandees = {};
    var lance = false;
    var n = liste.length;
    function charger(c) {
      if (!c || !c.image || demandees[c.image]) return;
      demandees[c.image] = true;
      var im = new fenetre.Image();
      im.decoding = "async";
      im.src = c.image;
    }
    function voisines(index) {
      if (!n) return;
      charger(liste[(index + 1) % n]);
      charger(liste[(index - 1 + n) % n]);
    }
    function quandInactif(fonction) {
      if (typeof fenetre.requestIdleCallback === "function") fenetre.requestIdleCallback(fonction, { timeout: 4000 });
      else fenetre.setTimeout(fonction, 1500);
    }
    return {
      // Image déjà demandée par la page (balise <img>) ou par l'affichage d'un coloris : jamais redemandée.
      dejaLa: function (c) { if (c && c.image) demandees[c.image] = true; },
      voisines: voisines,
      lancer: function (index) {
        if (lance) return;
        lance = true;
        voisines(index);
        quandInactif(function () { liste.forEach(charger); });
      },
      demandees: function () { return Object.keys(demandees); }
    };
  }

  function init(racine) {
    if (racine.dataset.hcPret) return;
    racine.dataset.hcPret = "1";

    var bloc = racine.querySelector("[data-hc-donnees]");
    if (!bloc) return;
    var donnees = JSON.parse(bloc.textContent);
    var coloris = donnees.coloris;
    var nb = coloris.length;

    var cartes = Array.prototype.map.call(racine.querySelectorAll("[data-hc-carte]"), function (el) {
      return { el: el, id: el.getAttribute("data-coloris"), offset: 0 };
    });
    var imgs = racine.querySelectorAll("[data-hc-img]");
    var filigranes = racine.querySelectorAll("[data-hc-filigrane] span");
    var statut = racine.querySelector("[data-hc-statut]");
    var annonce = racine.querySelector("[data-hc-annonce]"); // zone aria-live masquée : « Couleur choisie : … »
    var bouton = racine.querySelector("[data-hc-ajouter]");
    var compteur = racine.querySelector("[data-hc-compteur]");
    var metaTheme = document.querySelector('meta[name="theme-color"]');
    var silhouette = donnees.silhouette && window.LayalSilhouette ? window.LayalSilhouette(racine, donnees) : null;

    // Textes liés au JSON (marque, nom du produit…) : data-hc-champ="produit.nom" reçoit donnees.produit.nom.
    // Le HTML contient le même texte par défaut (affiché sans JS) ; le JSON reste la source à modifier.
    Array.prototype.forEach.call(racine.querySelectorAll("[data-hc-champ]"), function (el) {
      var valeur = el.getAttribute("data-hc-champ").split(".").reduce(function (o, cle) {
        return o && o[cle];
      }, donnees);
      if (typeof valeur === "string" && valeur) el.textContent = valeur;
    });

    var indexParId = {};
    coloris.forEach(function (c, i) { indexParId[c.id] = i; });

    // Image des cartes : champ « vignette » du JSON (échantillon de la maille du produit dans le coloris).
    cartes.forEach(function (carte) {
      var c = coloris[indexParId[carte.id]];
      var vignette = carte.el.querySelector("img");
      if (c && c.vignette && vignette && vignette.getAttribute("src") !== c.vignette) vignette.src = c.vignette;
    });

    // Images détourées des autres coloris : préchargées plus tard (prechargeurImages, branché en fin d'init).
    var prechargeur = prechargeurImages(coloris, window);

    // Réfraction SVG : seulement Chromium (Safari et Firefox gardent le flou simple).
    // userAgentData est vide hors contexte sécurisé (file://) : repli sur la chaîne d'agent
    // (Chrome, Edge, Opera… ; Chrome sur iOS tourne sur WebKit, donc exclu).
    var marques = (navigator.userAgentData && navigator.userAgentData.brands) || [];
    var ua = navigator.userAgent;
    var chromium = marques.some(function (m) { return /Chromium|Google Chrome/.test(m.brand); }) ||
      (/\bChrome\/\d+/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua));
    if (chromium && window.CSS && CSS.supports("backdrop-filter", 'url("#hc-verre")')) {
      racine.classList.add("a-refraction");
    }

    // Coloris de départ : ?couleur=bordeaux, sinon ?variant=<id> (variante d'une couleur), sinon le coloris par défaut
    // du JSON (couleur de la variante choisie par Shopify).
    var demande = null;
    var parametres = null;
    try { parametres = new URLSearchParams(window.location.search); } catch (e) { /* URL illisible */ }
    if (parametres) {
      demande = normaliser(parametres.get("couleur"));
      if (indexParId[demande] === undefined && parametres.get("variant")) {
        var idDemande = String(parametres.get("variant"));
        coloris.forEach(function (c) { if (String(c.variante) === idDemande) demande = c.id; });
      }
    }
    var courant = indexParId[demande] !== undefined ? indexParId[demande] : (indexParId[donnees.defaut] || 0);
    var imgActive = 0;
    var minuterieNormalisation = null;

    // ---------- Palette ----------
    function appliquerPalette(c) {
      racine.classList.toggle("hc--photo-fournie", c.photoStatique === true);
      Object.keys(VARIABLES).forEach(function (cle) {
        if (c.palette[cle]) racine.style.setProperty(VARIABLES[cle], c.palette[cle]);
      });
      if (metaTheme) metaTheme.setAttribute("content", c.palette.cadre);
      // Couleur du cadre exposée à la page (--hc-cadre sur <html>) : l'aperçu l'utilise comme fond sous le hero,
      // qui ne prend plus tout l'écran ; un thème peut s'en servir pour la section suivante.
      document.documentElement.style.setProperty("--hc-cadre", c.palette.cadre);
    }

    // ---------- Roue ----------
    function posOffset(carte, valeur, instantane) {
      if (instantane) carte.el.classList.add("sans-transition");
      carte.el.style.setProperty("--offset", String(valeur));
      carte.offset = valeur;
      if (instantane) {
        void carte.el.offsetWidth; // applique la position sans transition
        carte.el.classList.remove("sans-transition");
      }
    }

    // Ramène chaque carte dans la plage -2..+3 une fois la rotation terminée (sans animation : elle est cachée).
    function normaliserRoue() {
      cartes.forEach(function (carte) {
        var n = ((((carte.offset + 2) % nb) + nb) % nb) - 2;
        if (n !== carte.offset) posOffset(carte, n, true);
        carte.el.classList.toggle("est-cachee", Math.abs(n) >= CRAN_CACHE);
      });
    }

    function tournerRoue(delta, instantane) {
      cartes.forEach(function (carte) {
        var o = carte.offset - delta;
        posOffset(carte, o, instantane);
        carte.el.classList.toggle("est-cachee", Math.abs(o) >= CRAN_CACHE - 0.5);
      });
      clearTimeout(minuterieNormalisation);
      if (instantane) normaliserRoue();
      else minuterieNormalisation = setTimeout(normaliserRoue, dureeCss(racine, "--duree-roue", 650) + 30);
    }

    // Mise en page empilée (mobile, tablette en portrait) : même requête que le bloc mobile de hero-couleurs.css.
    var empile = window.matchMedia("(max-width: 760px), (max-width: 1100px) and (orientation: portrait)");
    var roue = racine.querySelector("[data-hc-roue]");
    var visuel = racine.querySelector("[data-hc-visuel]");

    // ---------- Carrousel mobile en boucle ----------
    // Les 6 cartes d'origine (boutons radio) restent au milieu ; COPIES séries de copies décoratives sont
    // ajoutées avant et après (div aria-hidden, hors du radiogroup pour les lecteurs d'écran, jamais focalisables).
    // Quand le défilement s'arrête sur une copie, on le ramène sans animation sur la série d'origine : les séries
    // sont identiques, le saut est invisible. Les copies sont masquées en CSS sur ordinateur.
    var COPIES = 2;
    function copier(el) {
      var d = document.createElement("div");
      d.className = "hc__pastille verre hc__pastille--copie";
      d.setAttribute("aria-hidden", "true");
      d.setAttribute("data-coloris", el.getAttribute("data-coloris"));
      d.appendChild(el.querySelector("img").cloneNode(true));
      return d;
    }
    var avant = document.createDocumentFragment();
    var apres = document.createDocumentFragment();
    for (var r = 0; r < COPIES; r++) {
      cartes.forEach(function (carte) {
        avant.appendChild(copier(carte.el));
        apres.appendChild(copier(carte.el));
      });
    }
    roue.insertBefore(avant, roue.firstChild);
    roue.appendChild(apres);
    var piste = Array.prototype.slice.call(roue.querySelectorAll(".hc__pastille")); // ordre du DOM : copies, originales, copies

    function majRadios() {
      cartes.forEach(function (carte) {
        var choisi = indexParId[carte.id] === courant;
        carte.el.setAttribute("aria-checked", choisi ? "true" : "false");
        carte.el.tabIndex = choisi ? 0 : -1;
      });
      piste.forEach(function (el) {
        el.classList.toggle("est-choisie", indexParId[el.getAttribute("data-coloris")] === courant);
      });
    }

    function carteDe(index) {
      for (var k = 0; k < cartes.length; k++) if (indexParId[cartes[k].id] === index) return cartes[k];
      return cartes[0];
    }

    function centreDe(el) { return el.offsetLeft + el.offsetWidth / 2; }

    // Carrousel (mise en page empilée) : amène une carte du coloris au centre.
    // - instantané : la carte d'origine ;
    // - animé : la carte indiquée (celle qu'on vient de toucher) ou, à défaut, l'exemplaire le plus proche
    //   du centre actuel (après Rose vient la copie de Noir juste à droite : la boucle se voit).
    function centrerCarte(index, instantane, cible) {
      if (!empile.matches || roue.scrollWidth <= roue.clientWidth) return;
      var sansAnimation = instantane || animationsReduites();
      var el = cible;
      if (!el && sansAnimation) el = carteDe(index).el;
      if (!el) {
        var centre = roue.scrollLeft + roue.clientWidth / 2;
        var meilleur = Infinity;
        piste.forEach(function (p) {
          if (indexParId[p.getAttribute("data-coloris")] !== index) return;
          var d = Math.abs(centreDe(p) - centre);
          if (d < meilleur) { meilleur = d; el = p; }
        });
      }
      roue.scrollTo({ left: centreDe(el) - roue.clientWidth / 2, behavior: sansAnimation ? "auto" : "smooth" });
      if (sansAnimation) recaler();
    }

    // Ramène le défilement sur la série d'origine s'il s'est arrêté sur une copie (saut d'un nombre entier de
    // séries : l'image ne bouge pas). Jamais pendant qu'un doigt est posé sur le carrousel.
    var doigtSurCarrousel = false;
    var minuterieRecalage = null;
    function recaler() {
      if (!empile.matches || doigtSurCarrousel) return;
      var centre = roue.scrollLeft + roue.clientWidth / 2;
      var k = 0, meilleur = Infinity;
      piste.forEach(function (p, i) {
        var d = Math.abs(centreDe(p) - centre);
        if (d < meilleur) { meilleur = d; k = i; }
      });
      var serie = Math.floor(k / nb);
      if (serie === COPIES) return;
      var periode = centreDe(piste[nb]) - centreDe(piste[0]);
      roue.scrollTo({ left: roue.scrollLeft - (serie - COPIES) * periode, behavior: "auto" });
    }
    function recalerBientot() {
      clearTimeout(minuterieRecalage);
      minuterieRecalage = setTimeout(recaler, 140); // repli sans « scrollend » (Safari ancien)
    }
    roue.addEventListener("scroll", recalerBientot, { passive: true });
    if ("onscrollend" in window) {
      roue.addEventListener("scrollend", function () { clearTimeout(minuterieRecalage); recaler(); });
    }
    roue.addEventListener("touchstart", function () { doigtSurCarrousel = true; }, { passive: true });
    roue.addEventListener("touchend", function () { doigtSurCarrousel = false; recalerBientot(); }, { passive: true });
    roue.addEventListener("touchcancel", function () { doigtSurCarrousel = false; recalerBientot(); }, { passive: true });

    // ---------- Produit, filigrane ----------
    function fondu(liste, texte) {
      var actif = liste[0].classList.contains("est-actif") ? 0 : 1;
      var suivant = 1 - actif;
      liste[suivant].textContent = texte;
      liste[suivant].style.setProperty("--lettres", String(texte.length));
      liste[suivant].classList.add("est-actif");
      liste[actif].classList.remove("est-actif");
    }

    var ETATS_IMG = ["est-sortant", "est-actif", "vers-gauche", "vers-droite", "sans-transition"];
    var jetonProduit = 0;
    // sens : 0 = fondu tournant (ordinateur) ; +1 / -1 = glissé mobile (+1 : sortie à gauche, entrée par la droite).
    function majProduit(c, instantane, sens) {
      var alt = donnees.produit.nom + ", coloris " + c.nom;
      var jeton = ++jetonProduit; // un clic plus récent annule l'affichage d'un décodage en retard
      if (instantane) {
        imgs[imgActive].src = c.image;
        imgs[imgActive].alt = alt;
        imgs[imgActive].classList.remove.apply(imgs[imgActive].classList, ETATS_IMG);
        imgs[imgActive].classList.add("est-actif");
        imgs[imgActive].removeAttribute("aria-hidden");
        imgs[1 - imgActive].classList.remove.apply(imgs[1 - imgActive].classList, ETATS_IMG);
        return;
      }
      var sortante = imgs[imgActive];
      var entrante = imgs[1 - imgActive];
      visuel.classList.toggle("en-glisse", !!sens);
      entrante.classList.remove.apply(entrante.classList, ETATS_IMG);
      if (sens) {
        // Position de départ du nouveau produit, sans animation : du côté opposé au geste.
        entrante.classList.add("sans-transition", sens > 0 ? "vers-droite" : "vers-gauche");
        void entrante.offsetWidth;
        entrante.classList.remove("sans-transition");
      }
      entrante.src = c.image;
      entrante.alt = alt;
      var montrer = function () {
        if (jeton !== jetonProduit) return;
        sortante.classList.remove("est-actif", "vers-gauche", "vers-droite");
        sortante.classList.add(sens ? (sens > 0 ? "vers-gauche" : "vers-droite") : "est-sortant");
        sortante.alt = "";
        sortante.setAttribute("aria-hidden", "true");
        entrante.removeAttribute("aria-hidden");
        entrante.classList.remove("est-sortant", "vers-gauche", "vers-droite");
        void entrante.offsetWidth;
        entrante.classList.add("est-actif");
      };
      if (entrante.decode) entrante.decode().then(montrer, montrer);
      else montrer();
      imgActive = 1 - imgActive;
    }

    // Adresse : ?variant=<id de la variante de cette couleur> (lien partageable, même règle que Shopify).
    function majUrl(c) {
      if (c.variante && window.Layal && window.Layal.majUrlVariante) {
        window.Layal.majUrlVariante(c.variante);
        return;
      }
      try {
        var url = new URL(window.location.href);
        url.searchParams.set("couleur", c.id);
        window.history.replaceState(window.history.state, "", url.toString());
      } catch (e) { /* contexte restreint : on ignore */ }
    }

    // Bouton : prix de la variante de la couleur affichée, ou « Épuisé » (désactivé) ; variante du formulaire.
    var textes = donnees.textes || {};
    var boutonTexte = racine.querySelector("[data-hc-ajouter-texte]");
    var boutonSeparateur = racine.querySelector("[data-hc-ajouter-separateur]");
    var boutonPrix = racine.querySelector("[data-hc-ajouter-prix]");
    var champVariante = racine.querySelector("[data-hc-variante]");
    var lot = donnees.lot && donnees.lot.quantite ? donnees.lot : null;
    function majBouton(c) {
      // Le lien vers les couleurs reste accessible, même si le coloris du hero est épuisé.
      if (bouton.hasAttribute("data-lp-choisir-quantite")) return;
      var dispo = c.disponible !== false && !!c.variante;
      var prixBouton = lot ? lot.prix : c.prix;
      bouton.disabled = !dispo;
      if (champVariante && c.variante) champVariante.value = c.variante;
      if (boutonTexte) boutonTexte.textContent = dispo ? (textes.ajouter || boutonTexte.textContent) : (textes.epuise || boutonTexte.textContent);
      if (boutonSeparateur) boutonSeparateur.textContent = dispo && prixBouton ? " · " : "";
      if (boutonPrix) boutonPrix.textContent = dispo ? (prixBouton || "") : "";
    }

    // ---------- Sélection ----------
    // options.externe : choix venu d'ailleurs sur la page (API heroCouleurs.choisirColoris, ci-dessous) ;
    // traité comme un choix de la visiteuse (arrêt du défilement, ?couleur=), sans annonce ni événement en retour.
    function choisir(index, options) {
      options = options || {};
      if (!options.auto) arreterAuto(); // tout choix de la visiteuse arrête le défilement automatique
      index = ((index % nb) + nb) % nb;
      if (index === courant && !options.force) return;
      var instantane = options.instantane || animationsReduites();

      // Plus court chemin sur la roue (-3..+3).
      var delta = index - courant;
      if (delta > nb / 2) delta -= nb;
      if (delta < -nb / 2) delta += nb;

      courant = index;
      var c = coloris[index];
      prechargeur.dejaLa(c); // demandée ci-dessous par majProduit
      appliquerPalette(c);
      majBouton(c);
      if (silhouette) silhouette.couleur(c, !options.auto && !options.initial);
      if (!options.initial) {
        tournerRoue(delta, instantane);
        majProduit(c, instantane, empile.matches ? options.sens || 0 : 0);
        fondu(filigranes, c.nom);
        // Défilement automatique : ni ?couleur= dans l'adresse, ni annonce (seuls les choix de la visiteuse).
        if (!options.auto) {
          majUrl(c);
          if (annonce && !options.externe) annonce.textContent = "Couleur choisie : " + c.nom;
        }
        // Événement « hc:coloris » : seulement pour un choix de la visiteuse DANS le hero (clic, clavier, glissé).
        // Jamais pour le défilement automatique ni pour un choix externe (pas de boucle avec le reste de la page).
        if (!options.auto && !options.externe) {
          racine.dispatchEvent(new CustomEvent("hc:coloris", { bubbles: true, detail: { id: c.id, nom: c.nom, variante: c.variante } }));
        }
      }
      majRadios();
      if (options.focus) carteDe(index).el.focus({ preventScroll: true });
      centrerCarte(index, instantane, options.cible);
      // Après l'image choisie : le coloris suivant et le précédent seront prêts au prochain geste.
      prechargeur.voisines(index);
    }

    // Position initiale des cartes (sans animation), cohérente avec le coloris de départ.
    cartes.forEach(function (carte) {
      var i = indexParId[carte.id];
      var o = ((((i - courant + 2) % nb) + nb) % nb) - 2;
      posOffset(carte, o, true);
      carte.el.classList.toggle("est-cachee", Math.abs(o) >= CRAN_CACHE);
    });
    var depart = coloris[courant];
    appliquerPalette(depart);
    majBouton(depart);
    if (silhouette) silhouette.couleur(depart, false);
    imgs[0].src = depart.image;
    prechargeur.dejaLa(depart);
    imgs[0].alt = donnees.produit.nom + ", coloris " + depart.nom;
    filigranes[0].textContent = depart.nom;
    filigranes[0].style.setProperty("--lettres", String(depart.nom.length));
    majRadios();
    centrerCarte(courant, true);
    empile.addEventListener("change", function () { centrerCarte(courant, true); });
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { racine.classList.remove("hc--depart"); });
    });

    var nouveauChoix = racine.querySelector("[data-hc-nouveau]");
    if (nouveauChoix) nouveauChoix.addEventListener("click", function () {
      var index = indexParId[nouveauChoix.getAttribute("data-hc-nouveau")];
      if (typeof index === "number") choisir(index, { focus: false });
    });
    // ---------- Interactions ----------
    // Un seul écouteur pour les cartes d'origine et leurs copies : la carte touchée est celle qu'on recentre.
    roue.addEventListener("click", function (e) {
      var el = e.target.closest(".hc__pastille");
      if (!el || !roue.contains(el)) return;
      var index = indexParId[el.getAttribute("data-coloris")];
      if (index === undefined) return;
      if (index === courant) centrerCarte(index, false, el);
      else choisir(index, { cible: el });
    });

    roue.addEventListener("keydown", function (e) {
      var pas = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
      if (pas) {
        e.preventDefault();
        choisir(courant + pas, { focus: true });
      } else if (e.key === "Home" || e.key === "End") {
        e.preventDefault();
        choisir(e.key === "Home" ? 0 : nb - 1, { focus: true });
      }
    });

    // Glissé horizontal sur toute la carte hero : coloris suivant (vers la gauche) ou précédent, en boucle.
    // - au doigt (ou au stylet) partout, sauf sur le carrousel qui garde son défilement natif ;
    // - à la souris, seulement sur le produit (pour ne pas gêner la sélection de texte) ;
    // - seuil 50 px et geste nettement horizontal : un appui simple n'est jamais un glissé ;
    // - le défilement vertical reste natif (touch-action: pan-y en CSS : le navigateur annule le geste).
    var carteHero = racine.querySelector(".hc__carte");
    var geste = null;
    var ignorerClic = false;
    carteHero.addEventListener("pointerdown", function (e) {
      geste = null;
      if (!e.isPrimary) return;
      if (empile.matches && roue.contains(e.target)) return;
      if (e.pointerType === "mouse" && !visuel.contains(e.target)) return;
      if (e.target.closest("input, textarea, select, button, [data-hc-menu]")) return;
      geste = { x: e.clientX, y: e.clientY, id: e.pointerId };
    });
    carteHero.addEventListener("pointerup", function (e) {
      if (!geste || e.pointerId !== geste.id) return;
      var dx = e.clientX - geste.x, dy = e.clientY - geste.y;
      geste = null;
      if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      ignorerClic = true; // le clic éventuel qui suit ce geste ne doit pas activer un bouton ou un lien
      setTimeout(function () { ignorerClic = false; }, 400);
      choisir(courant + (dx < 0 ? 1 : -1), { sens: dx < 0 ? 1 : -1 });
    });
    carteHero.addEventListener("pointercancel", function () { geste = null; });
    carteHero.addEventListener("click", function (e) {
      if (!ignorerClic) return;
      ignorerClic = false;
      e.preventDefault();
      e.stopPropagation();
    }, true);

    // ---------- Défilement automatique des coloris ----------
    // Coloris suivant toutes les « defilementAuto » ms (JSON ; 0 = désactivé), animé comme un choix normal
    // (roue sur ordinateur, glissé + recentrage du carrousel sur mobile), en boucle.
    // - jamais avec prefers-reduced-motion, ni si l'adresse contient ?couleur= (arrivée depuis une pub d'un coloris) ;
    // - arrêt définitif (jusqu'au rechargement) à la première interaction avec la carte hero : pointerdown (donc AVANT
    //   le clic sur « Ajouter au panier » : le coloris ajouté est bien celui affiché), clic, focus clavier, molette sur
    //   le carrousel, tout choix de coloris ;
    // - pause au survol à la souris du produit, des cartes ou du bouton pause, onglet caché, hero visible à moins de 50 % ;
    // - bouton pause / lecture (WCAG 2.2.2), qui relance aussi après un arrêt définitif ; anneau = temps restant.
    var boutonAuto = racine.querySelector("[data-hc-auto]");
    var boucleVideo = !!(silhouette && donnees.silhouette.boucle);
    // Vidéo sans boucle : le geste se joue au chargement (démonstration lancée par hero-video.js quand la
    // vidéo est prête), puis reste figé sur « imageFinale ». Chaque choix de coloris le rejoue depuis le début
    // avec la nouvelle couleur (démonstration relancée par hero-video.js dès que la nouvelle vidéo est prête),
    // puis il se fige de nouveau. Pas de défilement automatique des coloris.
    var gesteUnique = !!(silhouette && !boucleVideo);
    var intervalleAuto = Math.max(0, parseInt(donnees.defilementAuto, 10) || 0);
    if (boucleVideo) intervalleAuto = (donnees.silhouette.dureeVideo || 10) * 1000 / (Number(donnees.silhouette.vitesse) || 1);
    // La démonstration doit couvrir tout le geste (avec marge), même si defilementAuto est court ou nul.
    if (gesteUnique) intervalleAuto = Math.max(intervalleAuto, 2 * (donnees.silhouette.imageFinale || donnees.silhouette.dureeVideo || 10) * 1000 / (Number(donnees.silhouette.vitesse) || 1));
    var auto = { actif: false, gesteSeul: false, survol: false, cache: document.hidden, horsEcran: false, minuterie: null, debut: 0, reste: intervalleAuto };

    function autoTourne() { return auto.actif && (boucleVideo || !auto.survol || auto.gesteSeul) && !auto.cache && !auto.horsEcran && !animationsReduites(); }
    function suspendreMinuterie() {
      if (!auto.minuterie) return;
      clearTimeout(auto.minuterie);
      auto.minuterie = null;
      auto.reste = Math.max(0, auto.reste - (Date.now() - auto.debut));
    }
    // Recalcule l'état (minuterie, anneau, libellé du bouton) ; le temps déjà écoulé est conservé pendant une pause.
    function majAuto() {
      suspendreMinuterie();
      var tourne = autoTourne();
      if (silhouette) silhouette.synchroniser(tourne, intervalleAuto - auto.reste);
      if (tourne) {
        auto.debut = Date.now();
        auto.minuterie = setTimeout(pasAuto, auto.gesteSeul && !boucleVideo ? Math.max(0, auto.reste - intervalleAuto * 0.16) : auto.reste);
      }
      if (!boutonAuto) return;
      boutonAuto.classList.toggle("est-en-cours", auto.actif);
      boutonAuto.classList.toggle("est-suspendu", !tourne);
      boutonAuto.setAttribute("aria-pressed", auto.actif ? "false" : "true");
      var sujetAuto = silhouette ? "l’animation" : "le défilement des couleurs";
      boutonAuto.setAttribute("aria-label", (auto.actif ? "Mettre en pause " : "Reprendre ") + sujetAuto);
    }
    function relancerAnneau() {
      if (!boutonAuto) return;
      boutonAuto.classList.remove("est-en-cours");
      var progression = boutonAuto.querySelector(".hc__auto-progression");
      if (progression) progression.style.animationDelay = "-" + (intervalleAuto - auto.reste) + "ms";
      void boutonAuto.offsetWidth; // redémarre l'animation CSS de l'anneau
    }
    function pasAuto() {
      auto.minuterie = null;
      // La vidéo boucle nativement et garde le coloris choisi. L'anneau suit
      // sa durée accélérée ; aucune remise à zéro brutale du lecteur.
      if (boucleVideo) {
        auto.reste = intervalleAuto;
        relancerAnneau();
        majAuto();
        return;
      }
      if (auto.gesteSeul || gesteUnique) {
        auto.reste = intervalleAuto * 0.16;
        auto.actif = false;
        auto.gesteSeul = false;
        majAuto();
        return;
      }
      choisir(courant + 1, { auto: true, sens: 1 });
      auto.reste = intervalleAuto;
      relancerAnneau();
      majAuto();
    }
    function arreterAuto(origine) {
      if (!auto.actif) return;
      // Vidéo sans boucle : un contact ou un clic ne fige pas le maintien en plein vol ; le geste (quelques
      // secondes) se termine seul, et un choix de coloris le rejoue depuis le début (hero-video.js).
      // Seul le bouton pause l'interrompt.
      if (gesteUnique && origine !== "bouton") return;
      suspendreMinuterie();
      auto.actif = false;
      if (!silhouette) auto.reste = intervalleAuto;
      majAuto();
    }
    function lancerAuto() {
      suspendreMinuterie();
      auto.actif = true;
      auto.reste = intervalleAuto - (silhouette ? silhouette.tempsReprise() : 0);
      auto.survol = false; // la souris est sur le bouton : on ne reste pas en pause « survol » après avoir demandé la lecture
      relancerAnneau();
      majAuto();
    }

    // Une démonstration garde le coloris choisi et, si configurée, se répète.
    // La pause et la boucle utilisent la même horloge.
    racine.addEventListener("hc:demonstration", function () {
      suspendreMinuterie();
      auto.gesteSeul = true;
      auto.actif = !animationsReduites();
      auto.reste = animationsReduites() ? intervalleAuto * 0.16 : intervalleAuto;
      auto.survol = false;
      relancerAnneau();
      majAuto();
    });

    if (boutonAuto && intervalleAuto > 0 && !animationsReduites()) {
      var couleurDansUrl = false;
      try {
        var p = new URLSearchParams(window.location.search);
        couleurDansUrl = p.has("couleur") || p.has("variant");
      } catch (e) { /* URL illisible */ }
      var horsBouton = function (e) { return !boutonAuto.contains(e.target); };

      boutonAuto.hidden = false;
      boutonAuto.style.setProperty("--duree-auto", intervalleAuto + "ms");
      boutonAuto.addEventListener("click", function () {
        if (auto.actif) arreterAuto("bouton");
        else lancerAuto();
      });
      // Interactions de la visiteuse (phase de capture : avant les autres gestionnaires).
      ["pointerdown", "click"].forEach(function (type) {
        carteHero.addEventListener(type, function (e) { if (horsBouton(e)) arreterAuto(); }, true);
      });
      carteHero.addEventListener("focusin", function (e) { if (horsBouton(e)) arreterAuto(); });
      roue.addEventListener("wheel", arreterAuto, { passive: true });
      // Pauses temporaires. Survol à la souris : seulement le produit, les cartes de coloris et le bouton pause
      // (ailleurs sur le hero, le défilement continue). pointerover est redéclenché à chaque nouvel élément survolé.
      var ZONES_SURVOL = ".hc__visuel, .hc__pastille, [data-hc-auto]";
      var survolerZone = function (dedans) {
        if (dedans === auto.survol) return;
        auto.survol = dedans;
        majAuto();
      };
      carteHero.addEventListener("pointerover", function (e) {
        if (e.pointerType === "mouse") survolerZone(!!e.target.closest(ZONES_SURVOL));
      });
      carteHero.addEventListener("pointerleave", function (e) {
        if (e.pointerType === "mouse") survolerZone(false);
      });
      document.addEventListener("visibilitychange", function () { auto.cache = document.hidden; majAuto(); });
      if ("IntersectionObserver" in window) {
        new IntersectionObserver(function (entrees) {
          auto.horsEcran = entrees[entrees.length - 1].intersectionRatio < 0.5;
          majAuto();
        }, { threshold: [0, 0.5, 1] }).observe(carteHero);
      }
      // Geste unique : rien ne démarre ici, hero-video.js lance la démonstration quand la vidéo est prête.
      auto.actif = !gesteUnique && (boucleVideo || !couleurDansUrl);
      majAuto();
    }

    // Ajout au panier : une unité de la variante de la couleur affichée (POST /cart/add.js), puis tiroir panier.
    // Le formulaire (sans script : envoi classique vers /cart/add) est intercepté.
    var minuterieAjout = null;
    var formulaire = racine.querySelector("[data-hc-formulaire]");
    var erreur = racine.querySelector("[data-hc-erreur]");
    // Compteur du panier : nombre réel tenu par theme.js ([data-cart-count]) ; petite impulsion à chaque ajout.
    function pulserCompteur() {
      if (!compteur) return;
      compteur.classList.remove("est-pulse");
      void compteur.offsetWidth;
      compteur.classList.add("est-pulse");
    }
    document.addEventListener("cart:added", pulserCompteur);
    function ajouter(e) {
      if (e) e.preventDefault();
      var c = coloris[courant];
      if (!c.variante || c.disponible === false || bouton.getAttribute("aria-busy") === "true") return;
      if (lot) { ajouterLot(); return; }
      if (!window.Layal || !window.Layal.ajouterAuPanier) { if (formulaire) formulaire.submit(); return; }
      bouton.setAttribute("aria-busy", "true");
      if (erreur) { erreur.hidden = true; erreur.textContent = ""; }
      window.Layal.ajouterAuPanier([{ id: c.variante, quantity: 1 }], bouton).then(function () {
        bouton.classList.add("est-ajoute");
        statut.textContent = "Ajouté au panier : " + donnees.produit.nom + ", coloris " + c.nom + ".";
        clearTimeout(minuterieAjout);
        minuterieAjout = setTimeout(function () { bouton.classList.remove("est-ajoute"); }, 1800);
      }, function (err) {
        var message = (err && err.message) || "Le panier n'a pas pu être mis à jour.";
        if (erreur) { erreur.textContent = message; erreur.hidden = false; }
        statut.textContent = message;
      }).then(function () { bouton.removeAttribute("aria-busy"); });
    }
    // Formule du bloc d'achat (JSON « lot ») : même ajout que son bouton (lignes, propriétés, complément).
    function ajouterLot() {
      var produit = document.querySelector("[data-lp-produit]");
      function versLesOffres() { window.location.hash = "choisir-mon-lot"; }
      if (!produit || !produit.lpChoisirQuantite || !produit.lpAjouter || !produit.lpChoisirQuantite(lot.quantite)) {
        versLesOffres();
        return;
      }
      bouton.setAttribute("aria-busy", "true");
      if (erreur) { erreur.hidden = true; erreur.textContent = ""; }
      produit.lpAjouter().then(function (r) {
        if (r && r.ok) {
          bouton.classList.add("est-ajoute");
          clearTimeout(minuterieAjout);
          minuterieAjout = setTimeout(function () { bouton.classList.remove("est-ajoute"); }, 1800);
        } else if (r && r.message) {
          if (erreur) { erreur.textContent = r.message; erreur.hidden = false; }
          statut.textContent = r.message;
        } else {
          versLesOffres(); // offre ou coloris indisponible : la visiteuse voit le bloc d'achat
        }
      }).then(function () { bouton.removeAttribute("aria-busy"); });
    }
    if (formulaire) formulaire.addEventListener("submit", ajouter);
    else if (!bouton.hasAttribute("data-lp-choisir-quantite")) bouton.addEventListener("click", ajouter);

    // Lien secondaire avec une vidéo (data-hc-video, ex. « Voir la pose » du voile) : la vidéo s'ouvre en grand sur
    // la page, sans son, en boucle ; × ou Échap ferme. Sans cet attribut (maintien) : lien inchangé.
    var lienVideo = racine.querySelector(".hc__lien[data-hc-video]");
    if (lienVideo && typeof HTMLDialogElement === "function") {
      lienVideo.addEventListener("click", function (e) {
        e.preventDefault();
        var fenetre = document.createElement("dialog");
        fenetre.className = "hc-video-grande";
        fenetre.setAttribute("aria-label", lienVideo.textContent.trim());
        var video = document.createElement("video");
        video.src = lienVideo.getAttribute("data-hc-video");
        video.muted = true; video.loop = true; video.playsInline = true; video.autoplay = true; video.controls = true;
        video.setAttribute("playsinline", "");
        var fermer = document.createElement("button");
        fermer.type = "button";
        fermer.className = "hc-video-grande__fermer";
        fermer.setAttribute("aria-label", "Fermer la vidéo");
        fermer.textContent = "×";
        fermer.addEventListener("click", function () { fenetre.close(); });
        fenetre.addEventListener("close", function () { video.pause(); fenetre.remove(); lienVideo.focus(); });
        fenetre.addEventListener("click", function (ev) { if (ev.target === fenetre) fenetre.close(); });
        fenetre.appendChild(video);
        fenetre.appendChild(fermer);
        document.body.appendChild(fenetre);
        fenetre.showModal();
        var lecture = video.play();
        if (lecture && lecture.catch) lecture.catch(function () {});
      });
    }

    // ---------- API pour le reste de la page (ex. bloc d'achat de la fiche produit) ----------
    // racine.heroCouleurs.coloris()            -> id du coloris affiché ;
    // racine.heroCouleurs.choisirColoris(id)   -> même effet qu'un clic sur une carte (arrête le défilement auto),
    //                                             sans événement « hc:coloris » en retour ;
    // racine.heroCouleurs.colorisListe()      -> données des coloris du hero (id, nom, image…), pour aligner les
    //                                             images des autres sections sur celles du hero ;
    // racine.heroCouleurs.ajouterAuPanier()   -> impulsion du compteur (le nombre réel vient du panier Shopify).
    // Événements émis sur la racine (remontent jusqu'au document) : « hc:pret » (API disponible) et « hc:coloris »
    // (choix de la visiteuse dans le hero, detail = { id, nom, variante }). Sans écouteur, le hero se comporte à
    // l'identique.
    racine.heroCouleurs = {
      coloris: function () { return coloris[courant].id; },
      colorisListe: function () { return coloris.slice(); },
      choisirColoris: function (id) {
        var i = indexParId[normaliser(id)];
        if (i !== undefined) choisir(i, { externe: true });
      },
      ajouterAuPanier: pulserCompteur
    };
    racine.dispatchEvent(new CustomEvent("hc:pret", { bubbles: true }));

    // Autres images détourées : après la fin du chargement de la page, ou dès le premier geste sur le hero
    // (doigt, souris, clavier, molette sur le carrousel), le premier des deux.
    function lancerPrechargement() { prechargeur.lancer(courant); }
    if (document.readyState === "complete") lancerPrechargement();
    else window.addEventListener("load", lancerPrechargement, { once: true });
    ["pointerdown", "keydown", "focusin", "wheel"].forEach(function (type) {
      racine.addEventListener(type, lancerPrechargement, { once: true, passive: true, capture: true });
    });

    // Menu (sur mobile, il porte aussi les liens de navigation).
    var boutonMenu = racine.querySelector("[data-hc-menu-bouton]");
    var menu = racine.querySelector("[data-hc-menu]");
    function fermerMenu() {
      menu.hidden = true;
      boutonMenu.setAttribute("aria-expanded", "false");
    }
    boutonMenu.addEventListener("click", function () {
      var ouvert = boutonMenu.getAttribute("aria-expanded") === "true";
      menu.hidden = ouvert;
      boutonMenu.setAttribute("aria-expanded", ouvert ? "false" : "true");
      if (!ouvert) { var premier = menu.querySelector("a"); if (premier) premier.focus(); }
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && !menu.hidden) { fermerMenu(); boutonMenu.focus(); }
    });
    document.addEventListener("click", function (e) {
      if (!menu.hidden && !menu.contains(e.target) && !boutonMenu.contains(e.target)) fermerMenu();
    });

    // Recherche : vraie recherche de la boutique (formulaire vers /search) ; rien à faire ici.

    // Éditeur de thème : un bloc « Couleur » sélectionné affiche sa couleur.
    document.addEventListener("shopify:block:select", function (e) {
      var carte = e.target && e.target.closest && e.target.closest("[data-hc-carte]");
      if (!carte || !racine.contains(carte)) return;
      var i = indexParId[carte.getAttribute("data-coloris")];
      if (i !== undefined) choisir(i, { externe: true, instantane: true });
    });
  }

  function demarrer() {
    document.querySelectorAll("[data-hero-couleurs]").forEach(init);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", demarrer);
  else demarrer();
  // Éditeur de thème Shopify : réinitialiser quand la section est rechargée.
  document.addEventListener("shopify:section:load", demarrer);
})();
